<template>
  <div class="bg-white">
    <div class="mb-6">
      <h2 class="text-xl font-medium text-black">Prospectos</h2>
      <div class="flex items-center justify-between mt-1">
        <p class="text-sm font-normal text-gray-900">En esta sección encontrarás todos los prospectos asignados a esta propiedad.</p>
        <button
          v-if="props.prospects.length > 0"
          class="flex items-center text-sm font-medium space-x-2 text-white bg-tuin-100 hover:bg-tuin_hover px-5 py-2.5 rounded-lg"
           @click="openProspects(props.property_id)"
          >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M10 5.5C10.2652 5.5 10.5196 5.60536 10.7071 5.79289C10.8946 5.98043 11 6.23478 11 6.5V9.5H14C14.2652 9.5 14.5196 9.60536 14.7071 9.79289C14.8946 9.98043 15 10.2348 15 10.5C15 10.7652 14.8946 11.0196 14.7071 11.2071C14.5196 11.3946 14.2652 11.5 14 11.5H11V14.5C11 14.7652 10.8946 15.0196 10.7071 15.2071C10.5196 15.3946 10.2652 15.5 10 15.5C9.73478 15.5 9.48043 15.3946 9.29289 15.2071C9.10536 15.0196 9 14.7652 9 14.5V11.5H6C5.73478 11.5 5.48043 11.3946 5.29289 11.2071C5.10536 11.0196 5 10.7652 5 10.5C5 10.2348 5.10536 9.98043 5.29289 9.79289C5.48043 9.60536 5.73478 9.5 6 9.5H9V6.5C9 6.23478 9.10536 5.98043 9.29289 5.79289C9.48043 5.60536 9.73478 5.5 10 5.5Z" fill="white"/>
          </svg>
          <span class="ml-2">Agregar</span>
        </button>
      </div>
    </div>
    
    <!-- Grid de prospectos -->
    <div v-if="props.prospects.length > 0">
      <div class="flex flex-wrap gap-4">
        <div
          v-for="(prospect, index) in props.prospects"
          :key="prospect.id || index"
          class="bg-[#FFFBFC] rounded-lg border w-80 h-[145px] border-[#E2E8F0] p-4 shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer"
          @click="handleProspectClick(prospect)"
        >
          <!-- Información del prospecto -->
          <div class="flex items-center gap-3 h-full">
            <!-- Avatar con iniciales -->
            <div class="flex-shrink-0">
              <div class="w-[70px] h-[70px] rounded-full bg-gray-200 flex items-center justify-center">
                <span class="text-[22px] font-semibold text-gray-900">
                  {{ getInitials(prospect) }}
                </span>
              </div>
            </div>

            <!-- Datos del prospecto -->
            <div class="flex-1 min-w-0">
              <!-- Badge de estado -->
              <div class="mb-3">
                <span 
                  :class="[
                    'inline-block font-medium text-xs px-2 py-1 rounded-md', 
                    getBadgeBgForId(prospect.status), 
                    getBadgeTextForId(prospect.status)
                  ]"
                >
                  {{ getStatusName(prospect.status) }}
                </span>
              </div>
              <div class="text-base font-medium text-gray-800 truncate">
                {{ getFullName(prospect) }}
              </div>
              <div class="text-sm font-medium text-[#475569] mt-1">
                Teléfono: <span 
                  class="font-bold text-sm text-gray-700 truncate hover:text-tuin_hover overflow-hidden whitespace-nowrap cursor-pointer hover:underline transition"
                  @click.stop="openContactModal(prospect)"
                >{{ prospect.country_code || '+52' }} {{ prospect.phone || 'Sin teléfono' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Estado vacío -->
    <div v-else class="flex flex-col items-center justify-center text-center py-8">
      <div class="flex justify-center items-center mb-4">
        <img class="h-[140px]" src="@/assets/imgs/undraw_people_ka7y2.svg" alt="">
      </div>
      <p class="mt-1 text-sm text-gray-900 sm:w-96">
        {{ getEmptyStateDescription() }}
      </p>
      <button
        class="mt-5 flex items-center text-sm font-medium space-x-2 text-white bg-tuin-100 hover:bg-tuin_hover px-5 py-2.5 rounded-lg"
        @click="openProspects(props.property_id)"
        v-if="status_id !== 2 && status_id !== null"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M10 5.5C10.2652 5.5 10.5196 5.60536 10.7071 5.79289C10.8946 5.98043 11 6.23478 11 6.5V9.5H14C14.2652 9.5 14.5196 9.60536 14.7071 9.79289C14.8946 9.98043 15 10.2348 15 10.5C15 10.7652 14.8946 11.0196 14.7071 11.2071C14.5196 11.3946 14.2652 11.5 14 11.5H11V14.5C11 14.7652 10.8946 15.0196 10.7071 15.2071C10.5196 15.3946 10.2652 15.5 10 15.5C9.73478 15.5 9.48043 15.3946 9.29289 15.2071C9.10536 15.0196 9 14.7652 9 14.5V11.5H6C5.73478 11.5 5.48043 11.3946 5.29289 11.2071C5.10536 11.0196 5 10.7652 5 10.5C5 10.2348 5.10536 9.98043 5.29289 9.79289C5.48043 9.60536 5.73478 9.5 6 9.5H9V6.5C9 6.23478 9.10536 5.98043 9.29289 5.79289C9.48043 5.60536 9.73478 5.5 10 5.5Z" fill="white"/>
        </svg>
        <span class="ml-2">Agregar prospecto</span>
      </button>
    </div>
    <ModalLeadsComponent
      v-if="leadModal"
      :property-id="dbPropertyId" 
      :show-modal="leadModal"      
      @trigger-update="EventCloseModal"  
    />
    
    <ContactProspectModal
      :show="showContactModal"
      :prospect="selectedProspect"
      :slug="props.property_link"
      @close="closeContactModal"
    />
  </div>
</template>

<script setup>
import { ref } from "vue";
import ModalLeadsComponent from "./modal/ModalLeadsComponent.vue";
import ContactProspectModal from "./modal/ContactProspectModal.vue";
const props = defineProps({
  prospects: {
    type: Array,
    default: () => []
  },
  status_id: {
    type: [Number, String],
    default: null
  },
  property_id: {
    type: [Number, String],
    default: null
  },
  property_link: {
    type: String,
    default: null
  },
})
let dbPropertyId = ref(null);
let leadModal = ref(false);
let showContactModal = ref(false);
let selectedProspect = ref(null);

// Definición de emisores
const emit = defineEmits(['prospect-click'])

// Mapas de colores para badges (basados en KanbanBoard)
const badgeBgMap = {
  '1': 'bg-green-100',
  '2': 'bg-yellow-100',
  '3': 'bg-cyan-100',
  '4': 'bg-violet-100',
  '5': 'bg-orange-100',
  '6': 'bg-red-100',
}

const badgeTextMap = {
  '1': 'text-green-800',
  '2': 'text-yellow-800',
  '3': 'text-cyan-800',
  '4': 'text-violet-800',
  '5': 'text-orange-800',
  '6': 'text-red-800',
}

// Catálogo de estatus para obtener el nombre
const statuses = [
  { name: 'Pendiente de contactar', status: '1' },
  { name: 'En proceso', status: '2' },
  { name: 'Finalizado', status: '3' },
  { name: 'No aplica', status: '4' },
  { name: 'Perdido', status: '5' },
  { name: 'Descartado', status: '6' },
]

// Funciones de utilidad
const getBadgeBgForId = (id) => {
  return badgeBgMap[String(id)] || 'bg-gray-100'
}

const getBadgeTextForId = (id) => {
  return badgeTextMap[String(id)] || 'text-gray-800'
}

const getStatusName = (statusId) => {
  const status = statuses.find(s => String(s.status) === String(statusId))
  return status ? status.name : 'Sin estado'
}

const getFullName = (prospect) => {
  const parts = [prospect.name, prospect.last_name, prospect.second_last_name].filter(Boolean)
  return parts.join(' ') || 'Sin nombre'
}

const getInitials = (prospect) => {
  const fullName = getFullName(prospect)
  if (fullName === 'Sin nombre') return 'SN'
  
  const names = fullName.split(' ').filter(Boolean)
  if (names.length >= 2) {
    return (names[0][0] + names[1][0]).toUpperCase()
  } else if (names.length === 1) {
    return names[0].slice(0, 2).toUpperCase()
  }
  return 'SN'
}

const handleProspectClick = (prospect) => {
  emit('prospect-click', prospect)
}

const getEmptyStateDescription = () => {
  const statusId = props.status_id

  if (statusId === null || statusId === undefined) {
    return 'La información de tu propiedad está en borrador. Termina de completarla para agregar prospectos.'
  } else if (Number(statusId) === 2) {
    return 'Tu propiedad no está visible porque está desactivada. Actívala para agregar prospectos.'
  } else {
    return 'No tienes prospectos agregados, te invitamos a que agregues a tus prospectos'
  }

}

async function openProspects(propertyId) {
  dbPropertyId.value  = propertyId;
  leadModal.value   = true;
}

async function EventCloseModal(){
  dbPropertyId.value  = null;
  leadModal.value     = false;
}

function openContactModal(prospect) {
  selectedProspect.value = prospect;
  showContactModal.value = true;
}

function closeContactModal() {
  selectedProspect.value = null;
  showContactModal.value = false;
}
</script>

<style scoped>

</style>
